// /joined — "You're on the list" confirmation. Dark radar adaptation that reuses
// the 404 page's ring + crw-sweep motif and the --glow-signal-strong token.
// Tier-aware, but defaults to 'general' so a direct visit or a refresh (no router
// state) still renders cleanly.
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { SiteNav } from './hero';
import { BigFooter } from './footer';
import type { WaitlistTier } from '../components/use-waitlist-submit';

const TIERS = new Set<WaitlistTier>(['monthly', 'annual', 'founding', 'general']);

export function JoinedPage() {
  const location = useLocation();
  const raw = (location.state as { tier?: string } | null)?.tier;
  const tier: WaitlistTier = raw && TIERS.has(raw as WaitlistTier) ? (raw as WaitlistTier) : 'general';

  useEffect(() => { document.title = "You're on the list — ClipRadar"; }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden', padding: '140px 24px 80px' }}>
        {/* atmosphere — blueprint grid fading at the edges */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', pointerEvents: 'none' }}></div>

        <div className="crw-page-in" style={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, maxWidth: 460 }}>
          {/* signal-glow check, ringed + swept like the radar */}
          <div aria-hidden="true" style={{ position: 'relative', width: 132, height: 132, marginBottom: 4, display: 'grid', placeItems: 'center' }}>
            {[1, 0.72, 0.44].map((s, i) => (
              <span key={i} style={{ position: 'absolute', inset: `${(1 - s) * 50}%`, borderRadius: '50%', border: '1px solid rgba(74,227,255,0.18)' }}></span>
            ))}
            <span className="crw-sweep" style={{ position: 'absolute', inset: 0, opacity: 0.55 }}></span>
            <span style={{ position: 'relative', display: 'grid', placeItems: 'center', width: 60, height: 60, borderRadius: '50%', color: 'var(--signal-ink)', background: 'radial-gradient(circle at 35% 30%, var(--signal-300), var(--signal-500))', boxShadow: 'var(--glow-signal-strong)' }}>
              <CRWIcon d="check" size={28} sw={2.6} />
            </span>
          </div>

          <span className="cr-label" style={{ color: 'var(--signal-400)' }}>You're in</span>
          <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4.8vw, 50px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.08 }}>
            You're on the <span className="crw-shimmer">list</span>.
          </h1>

          <p style={{ margin: 0, maxWidth: 400, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>
            We'll email you the moment ClipRadar opens up — keep cutting until then.
          </p>

          {tier === 'founding' ? (
            <p style={{ margin: '4px 0 0', maxWidth: 400, fontSize: 13.5, lineHeight: 1.65, color: 'var(--signal-300)', fontFamily: 'var(--font-mono)', letterSpacing: '0.02em', textWrap: 'pretty' }}>
              You're in line for founding pricing ($199) — we'll lock it in when we open up.
            </p>
          ) : null}

          <Link to="/" style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--signal-400)', textDecoration: 'none' }}>
            ← Back to homepage
          </Link>
        </div>
      </main>
      <BigFooter />
    </div>
  );
}
